import React from 'react';
import Modal from './Modal';
import { formatDate } from '../utils/dateFormatter';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} title="Privacy Policy" onClose={onClose}>
      <div className="privacy-modal-content">
        <div className="privacy-text">
          <p><strong>Effective Date: {formatDate(new Date())}</strong></p>
          
          <h3>1. Information We Collect</h3>
          <p>We collect information you provide directly to us, such as when you create an account, use our services, or contact us for support.</p>
          
          <h4>1.1 Personal Information</h4>
          <ul>
            <li>Name, email address, and contact information</li>
            <li>Date of birth and gender (for health assessments)</li>
            <li>Wellness assessment responses and results</li>
            <li>Chat history with AI coaches</li>
            <li>Usage data and preferences</li>
          </ul>

          <h4>1.2 Wellness Information</h4>
          <ul>
            <li>Assessment responses and wellness data</li>
            <li>Physical wellness metrics (height, weight, BMI)</li>
            <li>Mental wellness and emotional wellbeing assessments</li>
            <li>Nutritional and lifestyle information</li>
            <li>Blood test results (if uploaded)</li>
          </ul>

          <h3>2. How We Use Your Information</h3>
          <ul>
            <li>To provide personalized health insights and recommendations</li>
            <li>To improve our AI coaches and assessment tools</li>
            <li>To communicate with you about your account and our services</li>
            <li>To ensure platform security and prevent fraud</li>
            <li>To comply with legal obligations</li>
          </ul>

          <h3>3. Information Sharing</h3>
          <p>We do not sell, trade, or rent your personal health information to third parties. We may share information only in the following circumstances:</p>
          
          <ul>
            <li><strong>With your consent:</strong> When you explicitly authorize us to share information</li>
            <li><strong>Service providers:</strong> With trusted third parties who assist in operating our platform (under strict confidentiality agreements)</li>
            <li><strong>Legal requirements:</strong> When required by law or to protect our rights and safety</li>
            <li><strong>Medical emergencies:</strong> When necessary to protect your health or safety</li>
          </ul>

          <h3>4. Data Security</h3>
          <ul>
            <li>We use industry-standard encryption to protect your data</li>
            <li>Wellness information is stored securely and access is restricted</li>
            <li>Regular security audits and updates to protect against breaches</li>
            <li>Employee access to wellness data is limited and monitored</li>
          </ul>

          <h3>5. Your Rights</h3>
          <p>You have the right to:</p>
          <ul>
            <li>Access and review your personal information</li>
            <li>Correct or update your information</li>
            <li>Delete your account and associated data</li>
            <li>Export your health data</li>
            <li>Opt out of certain data processing activities</li>
          </ul>

          <h3>6. Data Retention</h3>
          <ul>
            <li>We retain your information as long as your account is active</li>
            <li>Wellness assessment data is kept to provide ongoing insights</li>
            <li>You can request deletion of your data at any time</li>
            <li>Some information may be retained for legal or security purposes</li>
          </ul>

          <h3>7. Cookies and Tracking</h3>
          <ul>
            <li>We use cookies to improve your experience and analyze usage</li>
            <li>You can control cookie settings in your browser</li>
            <li>We do not use cookies to track you across other websites</li>
          </ul>

          <h3>8. Third-Party Services</h3>
          <p>Our platform may integrate with third-party services (like AI providers). These services have their own privacy policies, and we encourage you to review them.</p>

          <h3>9. Children's Privacy</h3>
          <p>Our services are not intended for children under 13. We do not knowingly collect personal information from children under 13.</p>

          <h3>10. Changes to This Policy</h3>
          <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by email or through our platform.</p>

          <h3>11. Contact Us</h3>
          <p>If you have questions about this Privacy Policy or our data practices, please contact us at privacy@lifepsensor.com</p>

          <div className="privacy-footer">
            <p><strong>Your privacy and the security of your health information are our top priorities. We are committed to protecting your data and being transparent about our practices.</strong></p>
          </div>
        </div>
        
        <div className="privacy-modal-actions">
          <button className="btn btn-primary" onClick={onClose}>
            I Understand
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PrivacyPolicyModal;

